import React, { useState } from 'react';
import {
  PaymentCalculatorData,
  ModalGeneric,
  ModalWrapper,
  IconNext,
  PaymentCalculator,
} from '@payright/web-components';
import { SCModalMerchantDDO } from '../css';
import Loader from '@merchant-portal/components/loader';
import styled from 'styled-components';
import { media, Button } from '@payright/web-components';
import { initNewPlan } from '../../../slices/plans';
import { useDispatch, useSelector } from 'react-redux';
import { updateSaleDetails } from '../../../slices/checkout';
import { useHistory } from 'react-router-dom';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { RootState } from '../../../slices';
import { getMerchantDDODetails } from '@merchant-portal/slices/merchant';
import MerchantDDOConsent from '@merchant-portal/pages/merchant-ddo-consent';
import { AppDispatch } from '@merchant-portal/util/store';
import FeeDetails from '../../../components/fee-details';
import getConstants from '../../../util/constants';
import { isEmptyObject, isUndefined } from '../../../util/helper/checks';
import { showHideItemsAndMenu } from '../../../util/helper';
import { useStoryblok } from '@merchant-portal/providers/storyblok-provider';
import { FeatureFlags, useFeatures } from '@merchant-portal/providers/features-provider';
import { MerchantRate } from '@merchant-portal/util/merchant-api';
import SCSpinner from '@merchant-portal/components/spinner';
import { useIsAu } from '@merchant-portal/util/hooks';

const { PAYMENT_FREQUENCIES } = getConstants();

const SCErrorMessage = styled.p`
  margin: 1.5em 0 1em;
  color: red;
  line-height: 1.8;
  font-size: 0.94rem;
  font-weight: bold;

  ${media.max.tablet} {
    font-size: 0.86em;
  }
`;

const PlanCalculator = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const formState = useSelector((state: RootState) => state.checkout);
  const loggedInUser = useSelector((state: RootState) => {
    return state.auth;
  });
  const { ddoBlockPlanCreationMessage } = useStoryblok();
  const [showDDOModal, setShowDDOModal] = useState(false);
  // If there is some error from DDO updation we need to grab that error and display on the modal
  const { ddoDetailsErrorMessage, DDOLoading } = useSelector((state: RootState) => state.merchant);

  // Need the state to get the storyblock data
  const [showMerchantDDOBlockPlan, setShowMerchantDDOBlockPlan] = useState<any | null>(null);

  const { flagsConfig } = useFeatures();
  const canCreateCheckoutAndPlan = flagsConfig[FeatureFlags.CHECKOUT_AND_PLAN_CREATION];
  const isAu = useIsAu();

  const { accessItems } = showHideItemsAndMenu();

  const userHasPermissionsToCreatePlan = accessItems.createPlan;
  const [isValid, setIsValid] = useState(false);

  type FormData = {
    paymentDetails: PaymentCalculatorData;
    merchantRef?: string;
  };

  const initializeNewPlan = (formData: FormData) => {
    // Ensure checkout is reset, we do not create checkout
    // for sales-details form pre-fill from plan calculator
    dispatch(initNewPlan());
    // Prepare payload for sending pre-fill sale-details
    dispatch(
      updateSaleDetails({
        paymentDetails: formData.paymentDetails,
      })
    );
    // Redirect user over to sale-details with pre-filled form fields
    history.push(`/plans/new/sale-details`);
  };

  const newPlanSubmitHandler = async (formData: FormData) => {
    //show popup if a merchant's ddo details are blank, and if the end date has passed
    await dispatch(getMerchantDDODetails())
      .then((ddoDetails) => {
        if (ddoDetails.shouldBlockLoanCreation) {
          setShowDDOModal(true);
        } else {
          setShowDDOModal(false);
          initializeNewPlan(formData);
        }
      })
      .catch((errorMessage: string) => {
        console.log(errorMessage);
      });
  };
  /* End Form Event Handlers */

  const reactHookForm = useForm<FormData>({
    defaultValues: {
      paymentDetails: {
        ...formState.paymentDetails,
        paymentPeriod: loggedInUser?.storeConfig?.defaultTerm,
      },
    },
  });

  // Close the modal pop up and set the DDO Consent is false
  const handleCloseModal = () => {
    setShowDDOModal(false);
  };

  const { handleSubmit, watch } = reactHookForm;
  const selectedPaymentFrequency = watch('paymentDetails').paymentFrequency;

  // Get 'merchant rates', and create local storage fallback. Lastly, empty object will be handled next.
  const merchantRates =
    JSON.parse(localStorage.getItem('merchantRates') || '{}') || loggedInUser.merchantRates;

  // We handle empty object here, advise user to 'reset login session'.
  const merchantRatesCheck = !isUndefined(merchantRates) && !isEmptyObject(merchantRates);

  const maxPurchaseAmount = merchantRatesCheck
    ? Math.max(...merchantRates.rates.map((rate: MerchantRate) => rate.maximumPurchase))
    : 0;
  return (
    <div className="checkout">
      {/* DDO STUFF */}
      <div>
        <ModalWrapper
          open={showDDOModal}
          handleClose={() => setShowDDOModal(true)}
          background="dark"
        >
          <SCModalMerchantDDO>
            {DDOLoading ? (
              <Loader />
            ) : (
              <ModalGeneric
                text={ddoBlockPlanCreationMessage}
                content={
                  <MerchantDDOConsent
                    closeModalProp={handleCloseModal}
                    showConsent={false}
                    showConfirm={false}
                  />
                }
                className="merchantDDO"
              ></ModalGeneric>
            )}
          </SCModalMerchantDDO>
        </ModalWrapper>
      </div>
      {/* DDO STUFF Ends*/}
      <div className="checkout-heading">Plan Calculator</div>
      {canCreateCheckoutAndPlan ? (
        <>
          <div className="checkout-text">
            This calculator is for plans up to ${maxPurchaseAmount} and will always be interest
            free.
          </div>
          <hr />
        </>
      ) : isAu ? (
        <div className="checkout-text">
          Effective from the 30th of October 2026, you will no longer be able to create new payment plans and therefore this calculator function has been removed.
        </div>
      ) : (
        <div className="checkout-text">
          Effective from the 20th of February 2023, you will no longer be able to create new payment
          plans in New Zealand and therefore this calculator function has been removed.
        </div>
      )}

      {merchantRatesCheck && canCreateCheckoutAndPlan ? (
        <FormProvider {...reactHookForm}>
          <form onSubmit={handleSubmit(newPlanSubmitHandler)}>
            <Controller
              name="paymentDetails"
              rules={{
                validate: (paymentDetails) =>
                  paymentDetails.saleAmount !== 0 &&
                  paymentDetails.depositPercent !== 'N/A' &&
                  paymentDetails.paymentTerm !== 'N/A',
              }}
              render={({ onChange, onBlur, value, name, ref }) => (
                <PaymentCalculator
                  type="merchant"
                  rates={merchantRates.rates}
                  paymentFrequencies={PAYMENT_FREQUENCIES}
                  establishmentFees={merchantRates.establishmentFees}
                  otherFees={merchantRates.otherFees}
                  updatePaymentDetails={onChange}
                  paymentDetails={value}
                  onValidationChange={setIsValid}
                />
              )}
            />
            <FeeDetails
              accountKeepingFee={merchantRates.otherFees.monthlyAccountKeepingFee}
              processingFee={merchantRates.otherFees.paymentProcessingFee}
              paymentFrequency={selectedPaymentFrequency}
            />
            {userHasPermissionsToCreatePlan && (
              <>
                <br />
                <div className="actions">
                  <Button
                    disabled={formState.loading || !isValid}
                    type="submit"
                    colour="primary"
                    icon={DDOLoading ? <SCSpinner /> : <IconNext />}
                    size="medium"
                    iconPosition="right"
                  >
                    Create Plan
                  </Button>
                </div>
              </>
            )}
          </form>
        </FormProvider>
      ) : (
        <>
          {canCreateCheckoutAndPlan && (
            <SCErrorMessage>
              Error: Failed to load 'loan calculator'. Please reset your login session.
            </SCErrorMessage>
          )}
        </>
      )}
    </div>
  );
};

export default PlanCalculator;
